import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { LINKS } from "@/lib/site";

const navItems = [
  { label: "How it works", href: "#how" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "The engine", href: "#system" },
  { label: "Pricing", href: "#pricing" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        document.querySelector('[data-testid="nav-menu-toggle"]')?.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 border-b border-ink transition-colors duration-300 ${
        scrolled ? "glass" : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 md:h-[76px] flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center shrink-0" data-testid="nav-logo">
          <img src="/logo-ink.png" alt="GTM Consultancy" className="h-8 md:h-9 w-auto" />
        </a>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-8 min-w-0">
          {navItems.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="font-mono text-sm xl:text-base font-medium uppercase tracking-[0.08em] text-ink hover:text-cobalt transition-colors duration-200 whitespace-nowrap"
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <a
            href={LINKS.cal}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:inline-flex items-center whitespace-nowrap bg-cobalt px-4 xl:px-5 py-2 xl:py-2.5 font-mono text-sm xl:text-base font-medium uppercase tracking-[0.1em] text-white border border-ink transition-all duration-200 hover:-translate-y-0.5 hover:shadow-hard active:translate-y-0 active:shadow-none"
            data-testid="nav-book-call"
          >
            Book a meeting
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden flex flex-col items-center justify-center gap-1.5 min-h-[44px] min-w-[44px] p-2"
            aria-label="Menu"
            aria-expanded={open}
            aria-controls="mobile-navigation"
            data-testid="nav-menu-toggle"
          >
            <span className={`h-0.5 w-6 bg-ink transition-transform ${open ? "translate-y-[8px] rotate-45" : ""}`} />
            <span className={`h-0.5 w-6 bg-ink transition-opacity ${open ? "opacity-0" : ""}`} />
            <span className={`h-0.5 w-6 bg-ink transition-transform ${open ? "-translate-y-[8px] -rotate-45" : ""}`} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            id="mobile-navigation"
            initial={{ height: 0 }}
            animate={{ height: "auto" }}
            exit={{ height: 0 }}
            className="lg:hidden overflow-hidden glass border-t border-ink/20"
            data-testid="nav-mobile"
          >
            <div className="px-6 py-4 flex flex-col gap-2 max-h-[calc(100dvh-80px)] overflow-y-auto">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className="flex items-center min-h-[44px] py-2 font-mono text-base uppercase tracking-[0.1em] text-ink"
                >
                  {item.label}
                </a>
              ))}
              <a
                href={LINKS.cal}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex w-fit items-center bg-cobalt px-5 py-2.5 font-mono text-base uppercase tracking-[0.12em] text-white border border-ink"
              >
                Book a meeting
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
